import { execFile } from "node:child_process";
import { promisify } from "node:util";
import type { NoriConfig } from "@mewbleh/nori-core";

const execFileAsync = promisify(execFile);
type Runner = (file: string, args: string[]) => Promise<{ stdout: string; stderr: string }>;
const run: Runner = async (file, args) => execFileAsync(file, args, { timeout: 30_000, maxBuffer: 1024 * 1024 });

export async function listNoriSandboxes(runner: Runner = run): Promise<{ available: boolean; containers: Array<Record<string, unknown>>; error?: string }> {
  try {
    const { stdout } = await runner("docker", ["ps", "-a", "--filter", "label=ai.nori.sandbox=true", "--format", "{{json .}}"]) ;
    const containers = stdout.split("\n").filter(Boolean).map((line) => JSON.parse(line) as Record<string, unknown>);
    return { available: true, containers };
  } catch (error) {
    return { available: false, containers: [], error: (error as Error).message };
  }
}

export function explainSandbox(config: NoriConfig, agentId = "main") {
  const agent = config.agents.list.find((entry) => entry.id === agentId);
  if (!agent) throw new Error(`Unknown agent: ${agentId}`);
  return {
    agentId,
    workspace: agent.workspace ?? config.agents.defaults.workspace,
    mode: "host-approved",
    containerIsolation: false,
    note: "Nori currently executes opt-in filesystem and shell tools on the host after durable operator approval; Docker sandbox execution is not enabled yet.",
    tools: config.tools,
  };
}

export async function recreateNoriSandboxes(force: boolean, runner: Runner = run): Promise<{ removed: string[] }> {
  if (!force) throw new Error("Refusing to recreate sandboxes without --force");
  const inventory = await listNoriSandboxes(runner);
  if (!inventory.available) throw new Error(inventory.error ?? "Docker is unavailable");
  const ids = inventory.containers.map((container) => String(container.ID ?? container.Id ?? "")).filter(Boolean);
  for (const id of ids) await runner("docker", ["rm", "-f", id]);
  return { removed: ids };
}
